import React, { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Form, Formik, FormikConsumer } from 'formik';

import {
  DevicesCategory,
  IaPromptName,
  IaTitleAndDescriptionProposal,
  NewQuota,
  RequiredMediaSource,
  Session,
  SessionFormat,
  SessionQuota,
  StudyType,
  useQuotas,
} from '@app/api';
import {
  useIsAdmin,
  useMarkStudyStepAsValidated,
  useUpdateStudy,
} from 'queries';
import {
  InformationBox,
  Input,
  LabeledSection,
  NumberInput,
  TextArea,
  Toggle,
  TooltipText,
} from 'components';

import { EditSessionLayout } from './components/EditSessionLayout';
import { SessionFormSection } from './components/SessionFormSection';
import { useFormikSubmit } from 'hooks/useFormikSubmit';
import { SessionSubmitLine } from './components/SessionSubmitLine';
import { useRedirectIfDraft } from './hooks/useRedirectIfDraft';
import { useIaSuggestion } from './hooks/useIaSuggestion';
import { SessionTechnicalNeeds } from './components/SessionTechnicalNeeds';
import { SessionFormSectionSubsection } from './components/SessionFormSectionSubsection';
import { IncentiveField } from './components/IncentiveField';
import { IaProposals } from './components/IaProposals';
import { IaSuggestionButton } from './components/IaSuggestionButton';
import { DurationRangeSelectors } from 'components/Duration';
import { useTranslation } from 'react-i18next';

interface Props {
  session: Session;
}

const sessionToValues = (session: Session, quotas?: NewQuota[]) => ({
  title: session.title,
  description: session.description,
  duration: session.duration,
  durationDisplayed: session.durationDisplayed,
  showHowItWorks: session.showHowItWorks,
  allowJoinPanel: session.allowJoinPanel,
  requiredMediaSource: session.requiredMediaSource || [],
  allowedDevicesCategories: session.allowedDevicesCategories || [],
  allowedDevicesCategoriesQuotas: quotas || [],
  incentiveText: session.incentiveText,
});

export const EditSessionPublic = ({ session }: Props) => {
  const { t } = useTranslation();
  const shouldDisplayMediaSource =
    session.studyType === StudyType.MODERATED &&
    session.format === SessionFormat.VIDEO_CALL;
  const shouldDisplayTechnicalRequirements =
    shouldDisplayMediaSource || session.studyType === StudyType.UNMODERATED;
  const isAdmin = useIsAdmin();
  const hasParticipant = session.numberOfBooked > 0;

  const { mutateAsync: updateStudy } = useUpdateStudy(session.id);
  const { mutateAsync: markStepAsValidated } = useMarkStudyStepAsValidated(
    session.id,
  );

  const {
    available: iaProposalsAvailable,
    attempts: iaProposalsAttempts,
    ready: iaProposalsReady,
    request: requestIaProposals,
    requestFirst: requestFirstIaProposals,
    generating: iaProposalsGenerating,
  } = useIaSuggestion<{ proposals: IaTitleAndDescriptionProposal[] }>(
    session,
    IaPromptName.TITLE_AND_DESCRIPTION,
  );

  const hasProposals = iaProposalsAttempts.length > 0;
  const proposals = iaProposalsAttempts.map((p) => p.result?.proposals);
  const proposalsLoading = iaProposalsGenerating && !hasProposals;
  const [iaProposalsOpen, setIaProposalsOpen] = useState(false);
  const showProposals = iaProposalsOpen && (proposalsLoading || hasProposals);
  const showProposalButton =
    !showProposals && session.title == '' && session.description == '';

  const { data: quotas } = useQuotas(session.id, [
    SessionQuota.ALLOWED_DEVICES_CATEGORY,
  ]);

  const update = useCallback(
    async (values: ReturnType<typeof sessionToValues>) => {
      const session = await updateStudy(values);
      await markStepAsValidated({ step: 'public' });
      return session;
    },
    [markStepAsValidated, updateStudy],
  );

  const onSuccess = useRedirectIfDraft(session);

  const sessionToValuesWithQuotas = useCallback(
    (session: Session) => sessionToValues(session, quotas),
    [quotas],
  );
  const [initialValues, submit] = useFormikSubmit(
    update,
    session,
    sessionToValuesWithQuotas,
    { onSuccess, noSetValues: true },
  );

  const settingsValidationSchema = useMemo(() => {
    return Yup.object().shape({
      title: Yup.string().required(),
      description: Yup.string().required(),
      duration: Yup.number().required().min(1),
      requiredMediaSource: Yup.array().of(
        Yup.string().oneOf(Object.keys(RequiredMediaSource)),
      ),
      allowedDevicesCategories: shouldDisplayTechnicalRequirements
        ? Yup.array()
            .of(Yup.string().oneOf(Object.keys(DevicesCategory)))
            .min(1)
            .required()
        : Yup.array()
            .of(Yup.string().oneOf(Object.keys(DevicesCategory)))
            .min(0),
    });
  }, [shouldDisplayTechnicalRequirements]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submit}
      validationSchema={settingsValidationSchema}
      enableReinitialize
    >
      <Form>
        <EditSessionLayout
          session={session}
          footer={
            <FormikConsumer>
              {({ values }) => (
                <SessionSubmitLine
                  session={session}
                  duration={values.duration}
                />
              )}
            </FormikConsumer>
          }
        >
          <SessionFormSection
            title={t('editSession.public.title')}
            withWarningBox={!!session && session.published}
            hasParticipant={hasParticipant}
          >
            <InformationBox
              text={t('editSession.public.publicInfoBox')}
              withMargin
            />
            {iaProposalsAvailable && showProposalButton && (
              <IaSuggestionButton
                text={t('editSession.public.iaProposals.generate')}
                isLoading={!iaProposalsReady || iaProposalsGenerating}
                onClick={() => {
                  requestFirstIaProposals();
                  setIaProposalsOpen(true);
                }}
              />
            )}
            <Input
              name="title"
              label={t('sessions.display.title')}
              placeholder={t('sessions.placeholder.title')}
            />
            {showProposals && (
              <IaProposals
                field="title"
                proposals={proposals}
                loading={proposalsLoading}
                generating={iaProposalsGenerating}
                onRetry={requestIaProposals}
              />
            )}
            <TextArea
              name="description"
              label={t('sessions.display.description')}
              placeholder={t('sessions.placeholder.description')}
            />
            {showProposals && (
              <IaProposals
                field="description"
                proposals={proposals}
                loading={proposalsLoading}
                generating={iaProposalsGenerating}
                onRetry={requestIaProposals}
              />
            )}
            {session.studyType === StudyType.MODERATED &&
              (hasParticipant && !isAdmin ? (
                <TooltipText
                  title={t('editSession.public.disabledDuration')}
                  requireDiv
                >
                  <NumberInput
                    name="duration"
                    type="number"
                    label={t('sessions.display.duration')}
                    min={0}
                    step={15}
                    disabled
                  />
                </TooltipText>
              ) : (
                <NumberInput
                  name="duration"
                  type="number"
                  label={t('sessions.display.duration')}
                  min={0}
                  step={15}
                />
              ))}
            {session.studyType === StudyType.UNMODERATED && (
              <LabeledSection
                label={t('sessions.display.duration')}
                color="ink"
              >
                <DurationRangeSelectors
                  small
                  name="duration"
                  buttonDurations={[10, 20, 30, 60]}
                />
              </LabeledSection>
            )}
            <IncentiveField session={session} />

            {isAdmin && (
              <Toggle
                name="showHowItWorks"
                label={t('sessions.display.showHowItWorks')}
              />
            )}
            {isAdmin && (
              <Toggle
                name="allowJoinPanel"
                label={t('sessions.display.allowJoinPanel')}
              />
            )}
          </SessionFormSection>
          {shouldDisplayTechnicalRequirements && (
            <SessionFormSectionSubsection
              title={t('sessions.display.technicalNeeds')}
            >
              <SessionTechnicalNeeds
                displayMediaSource={shouldDisplayMediaSource}
                numberOfParticipantRequired={
                  session.numberOfParticipantRequired
                }
              />
            </SessionFormSectionSubsection>
          )}
        </EditSessionLayout>
      </Form>
    </Formik>
  );
};
